import { useLayoutEffect, useMemo, useState } from "react";
import { useDebouncedValue, useElementSize } from "@mantine/hooks";

import {
  COMPANY_GRID_WIDTH_DEBOUNCE_MS,
  companyGridColumnCount,
  estimateCompanyGridContainerWidth,
} from "@/pages/discover/companyGridPageSize.util";

function readContainerWidth(el: HTMLDivElement | null): number {
  if (!el) return 0;
  return el.getBoundingClientRect().width;
}

/** Observe compact company-grid container width and map it to the same column count as the CSS grid. */
export function useCompactCompanyGridColumns() {
  const { ref, width: observedWidth } = useElementSize<HTMLDivElement>();
  const [syncWidth, setSyncWidth] = useState(0);

  useLayoutEffect(() => {
    const remeasure = () => {
      const width = readContainerWidth(ref.current);
      if (width > 0) {
        setSyncWidth((prev) => (prev === width ? prev : width));
      }
    };

    remeasure();
    window.addEventListener("pageshow", remeasure);
    document.addEventListener("visibilitychange", remeasure);

    return () => {
      window.removeEventListener("pageshow", remeasure);
      document.removeEventListener("visibilitychange", remeasure);
    };
  }, [observedWidth, ref]);

  const measuredWidth = Math.max(observedWidth, syncWidth);
  const displayWidth =
    measuredWidth > 0 ? measuredWidth : estimateCompanyGridContainerWidth();
  const [debouncedMeasuredWidth] = useDebouncedValue(measuredWidth, COMPANY_GRID_WIDTH_DEBOUNCE_MS);
  const debouncedWidth =
    debouncedMeasuredWidth > 0 ? debouncedMeasuredWidth : displayWidth;

  const columns = useMemo(() => companyGridColumnCount(displayWidth), [displayWidth]);
  const debouncedColumns = useMemo(
    () => companyGridColumnCount(debouncedWidth),
    [debouncedWidth],
  );

  return { ref, width: displayWidth, columns, debouncedColumns };
}
